import React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, Trash2, User, Home, Users, Check, X as XIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';
import QuickApplicationForm from './QuickApplicationForm';
import AudioPlayer from './AudioPlayer';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { toggleJobCompletion } from '../../lib/supabase/jobs';
import type { Job } from '../../types/jobs';

interface JobCardProps {
  job: Job;
  onApply: (jobId: string) => void;
  onDelete?: (jobId: string) => void;
  canDelete?: boolean;
  applicationCount?: number;
  onToggleComplete?: (jobId: string, isCompleted: boolean) => void;
}

export default function JobCard({ job, onApply, onDelete, canDelete, applicationCount = 0, onToggleComplete }: JobCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showQuickApply, setShowQuickApply] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const { saveScrollPosition } = useScrollPosition({
    key: 'jobsListScroll',
    storage: 'session'
  });

  const handleClick = () => {
    saveScrollPosition();
    navigate(`/jobs/${job.id}`);
  };

  const handleToggleComplete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isToggling) return;

    setIsToggling(true);
    const newStatus = !job.isCompleted;

    const { error } = await toggleJobCompletion(job.id, newStatus);

    if (!error && onToggleComplete) {
      onToggleComplete(job.id, newStatus);
    }

    setIsToggling(false);
  };

  const getHousingLabel = (housing: Job['housing']) => {
    switch (housing) {
      case 'provided':
        return 'Housing Provided';
      case 'not-provided':
        return 'No Housing';
      case 'maybe':
        return 'Housing May Be Available';
      default:
        return 'No Housing Information';
    }
  };

  return (
    <div onClick={handleClick} className="block transform transition hover:-translate-y-1 cursor-pointer group">
      <div className="relative bg-white rounded-lg overflow-hidden shadow-[0_2px_10px_-2px_rgba(0,0,0,0.15)] border-2 border-gray-100">
        {job.photoUrl && (
          <div className="relative h-48 w-full border-b-4 border-green-600">
            <img
              src={job.photoUrl}
              alt={job.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            {applicationCount > 0 && (
              <div className="absolute top-3 right-3 bg-red-500 text-white rounded-full px-3 py-1.5 shadow-lg flex items-center space-x-1.5 font-bold text-sm">
                <Users className="h-4 w-4" />
                <span>{applicationCount}</span>
              </div>
            )}
          </div>
        )}
        {!job.photoUrl && applicationCount > 0 && (
          <div className="absolute top-3 right-3 bg-red-500 text-white rounded-full px-3 py-1.5 shadow-lg flex items-center space-x-1.5 font-bold text-sm z-10">
            <Users className="h-4 w-4" />
            <span>{applicationCount}</span>
          </div>
        )}
        <div className="p-5">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-2">
              {canDelete && (
                <>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete?.(job.id);
                    }}
                    className="text-gray-400 hover:text-red-500 transition-all duration-300 opacity-0 group-hover:opacity-100"
                    title="Delete job"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleToggleComplete}
                    disabled={isToggling}
                    className={`p-1.5 rounded transition-all duration-300 opacity-0 group-hover:opacity-100 ${
                      job.isCompleted
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={job.isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                  >
                    {job.isCompleted ? <Check className="h-5 w-5" /> : <XIcon className="h-5 w-5" />}
                  </button>
                </>
              )}
            </div>
            <div className="flex-1 text-center">
              <h3 className={`text-xl font-bold text-gray-900 tracking-tight ${job.isCompleted ? 'line-through text-gray-500' : ''}`}>
                {job.title}
              </h3>
            </div>
            <div className="w-[72px]"></div>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 uppercase tracking-wide">
              {job.type}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 uppercase tracking-wide">
              <Home className="h-3 w-3 mr-1" />
              {getHousingLabel(job.housing)}
            </span>
          </div>

          {job.audioMessageUrl && (
            <div className="mt-3">
              <AudioPlayer audioUrl={job.audioMessageUrl} compact showLabel />
            </div>
          )}

          <p className="mt-3 text-gray-600 text-sm line-clamp-3 whitespace-pre-line">{job.description}</p>
          
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-1.5 text-gray-400" />
              <span className="font-medium">{job.location}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <span className="font-medium">
                {job.payRate === 'DOE' ? 'DOE' : `$${job.payRate}/hour`}
              </span>
            </div>
            <div className="flex items-center text-gray-600">
              <span className="font-medium">
                {new Date(job.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className="mt-4">
            <Button
              variant="primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (user) {
                  onApply(job.id);
                } else {
                  setShowQuickApply(true);
                }
              }}
              fullWidth
              className="font-bold tracking-wide uppercase text-sm"
              disabled={job.status !== 'open'}
            >
              {job.status === 'open' ? 'Apply Now' : 'Position Filled'}
            </Button>
          </div>
        </div>
      </div>
      
      {showQuickApply && (
        <QuickApplicationForm
          job={job}
          onClose={() => setShowQuickApply(false)}
        />
      )}
    </div>
  );
}